import type { Definition } from 'typescript-json-schema';
import type { ImportObjectPageV4Parameters, Manifest, v4 } from '@sap/ux-specification-types';
import {
    ActionType,
    DefinitionName,
    FACETTITLEPREFIX,
    LogSeverity,
    MANIFESTPATH,
    ManifestSection,
    PageTypeV4,
    PropertyName
} from '@sap/ux-specification-types';
import { UIAnnotationTerms, UIAnnotationTypes } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { importSettingsOfObject } from '../utils';
import { DEFINITION_LINK_PREFIX, getDefinitionKey } from '../../../common';
import i18next from 'i18next';
import { log } from '../../../../extensionLogger';
import { initI18n } from '../../../../i18n/i18n';
import { MetadataInstanceFactoryV4 as Factory } from '../../export/factory';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import { createCustomFragmentConfig } from './fpmCustomPage';
import type { SapUiGenericAppPageSettings } from '@sap/ux-specification-types/src';
import { importActionSettingsOfReference, type ImportActionSettingsOfReferenceParams } from '../../utils/utils';
import { SchemaTag } from '@sap/ux-specification-types/src/common/types';

type ManifestContentSegment = string | number | boolean | object;

/**
 * Converts section ID to manifest ID (replaces '::' with ['/', '#']).
 *
 * @param sectionId - converted ID of the given object page section
 * @param splitOnLt - split ID and get only part in <...>
 * @param annotationTerm - annotationTerm to split Id on
 * @returns manifest ID
 */
export function getManifestId(sectionId: string, splitOnLt = false, annotationTerm?: string) {
    const id = splitOnLt ? sectionId.split('<')[1]?.slice(0, -1) : sectionId;
    if (annotationTerm) {
        const pathParts = id.split(`@${annotationTerm}`);
        return `${pathParts[0]?.replace(/::/g, '/')}@${annotationTerm}${pathParts[1]?.replace(/::/g, '#')}`;
    }

    return `${sectionId?.replace(/::/g, '/').replace(/::/g, '#')}`;
}

/**
 * Converts manifest ID to definition ID (replaces ['#', '/'] with '::').
 *
 * @param manifestId - ID of the given object page section in manifest
 * @returns definition ID
 */
export function getDefinitionId(manifestId: string) {
    return manifestId.replace(/#/g, '::').replace(/\//g, '::');
}

/**
 * Fills the section table columns in a V4 config.
 *
 * @param importParameters  - object comprising all input data
 * @param manifestSection - section of the manifest.json
 * @param configPart - the given part of the object page config
 * @param sectionId - converted ID of the given object page section
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param manifestSectionId - ID of the given object page section in manifest
 */
function fillTableColumnsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSection: ManifestContentSegment,
    configPart: object,
    sectionId: string,
    factory: MetadataInstanceInterface,
    manifestSectionId: string
) {
    if (manifestSection['columns']) {
        configPart[sectionId].table.columns = {};
        let tableDefinition: DefinitionName;
        const lineItems = importParameters.jsonSchema.definitions[getDefinitionId(manifestSectionId)] as Definition;
        if (lineItems) {
            for (const columnKey in lineItems.properties) {
                const columnInSchema = lineItems.properties[columnKey] as Definition;
                if (columnInSchema.$ref.indexOf(DefinitionName.CustomColumnOP) > -1) {
                    tableDefinition = DefinitionName.CustomColumnOP;
                } else {
                    tableDefinition = DefinitionName.ObjectPageTableColumn;
                }
                const columnInConfig = factory.createInstance(PageTypeV4.ObjectPage, tableDefinition);

                importSettingsOfObject(
                    columnInConfig,
                    importParameters.manifest,
                    importParameters.jsonSchema.definitions[tableDefinition],
                    importParameters.routingId,
                    [columnKey],
                    manifestSectionId
                );
                if (Object.keys(columnInConfig).length > 0) {
                    configPart[sectionId].table.columns[columnKey] = columnInConfig;
                }
            }
        }
    }
}

/**
 * Fills table actions and custom section actions in config.json.
 *
 * @param {ImportObjectPageV4Parameters} importParameters - object comprising all input data
 * @param {Definition} actionsDefinition - actions definition from schema
 * @param {string} sectionId - ID of the current section
 * @param {object} configPart - the given part of the object page config
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param manifestSectionId - section id in manifest
 */
function fillSectionActionsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    actionsDefinition: Definition,
    sectionId: string,
    configPart: object,
    factory: MetadataInstanceInterface,
    manifestSectionId: string
) {
    if (typeof actionsDefinition === 'object') {
        for (const actionKey in actionsDefinition['properties']) {
            const actionDefinition = actionsDefinition.properties[actionKey];
            if (typeof actionDefinition === 'object' && actionDefinition.$ref) {
                const definitionKey = getDefinitionKey(actionDefinition.$ref);
                const definition = actionsDefinition?.['properties']?.[actionKey] as Definition;
                const schemaPropertyName = definitionKey.split('<')[0];
                let configActionsPart;
                if (schemaPropertyName.includes(DefinitionName.CustomSectionActionOP)) {
                    // custom section actions
                    configActionsPart = configPart['actions'];
                } else {
                    // table toolBar actions
                    configActionsPart = configPart[sectionId].table.toolBar.actions;
                }
                const importActionParams: ImportActionSettingsOfReferenceParams = {
                    actionPropertyDefinition: definition,
                    appSchema: importParameters.jsonSchema,
                    factory,
                    actionsInConfig: configActionsPart,
                    actionKey,
                    manifest: importParameters.manifest,
                    routingId: importParameters.routingId,
                    manifestSectionId
                };
                importActionSettingsOfReference(importActionParams);
            }
        }
    }
}

/**
 * Determines if the provided action definition is of a specific action type or matches specific criteria.
 *
 * @param actionDefinition The action definition object to evaluate, which may contain properties like actionType.
 * @param formActionDefinition The form action definition object to evaluate, which may contain annotationType information.
 * @returns A boolean indicating whether the given action or form action definition meets the specified conditions for being a recognized action type.
 */
function isSupportedAction(actionDefinition: Definition, formActionDefinition: Definition) {
    const isAction = formActionDefinition[SchemaTag.annotationType] === UIAnnotationTypes.DataFieldForAction;
    const isActionGroup = formActionDefinition[SchemaTag.annotationType] === UIAnnotationTypes.DataFieldForActionGroup;
    const isCustomActionMenu = actionDefinition?.[SchemaTag.actionType] === ActionType.CustomMenu;
    const isCustomAction = formActionDefinition.description === 'Custom Action';
    return isAction || isActionGroup || isCustomAction || isCustomActionMenu;
}

/**
 * Fills form  actions in config.json.
 *
 * @param importParameters - object comprising all input data
 * @param manifestSection - section of the manifest.json containing the relevant settings
 * @param sectionId - ID of the current section
 * @param configPart - the given part of the object page config
 * @param factory - factory, for generating the access to reflect-metadata
 * @param manifestSectionId - ID of the section in the manifest
 */
function fillFormActionsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSection: ManifestContentSegment,
    sectionId: string,
    configPart: object,
    factory: MetadataInstanceInterface,
    manifestSectionId: string
) {
    if (manifestSection['actions']) {
        const configActionsPart = (configPart[sectionId].form.actions = {});
        const forms = importParameters.jsonSchema.definitions[
            `${DefinitionName.ObjectPageSectionForm}<${getDefinitionId(manifestSectionId)}>`
        ] as Definition;
        const formReference = forms.properties.form['$ref'].split(DEFINITION_LINK_PREFIX)[1];
        const form = importParameters.jsonSchema.definitions[formReference] as Definition;
        const actions = form.properties['actions'] as Definition;
        const actionsReference = actions.$ref.split(DEFINITION_LINK_PREFIX)[1];
        const actionsDefinition = importParameters.jsonSchema.definitions[actionsReference] as Definition;

        for (const actionKey in actionsDefinition['properties']) {
            const actionDefinition = actionsDefinition.properties[actionKey] as Definition;
            const definitionKey = getDefinitionKey(actionDefinition.$ref);
            const formActionDefinition = importParameters.jsonSchema.definitions[definitionKey] as Definition;

            if (isSupportedAction(actionDefinition, formActionDefinition)) {
                const importActionParams: ImportActionSettingsOfReferenceParams = {
                    actionPropertyDefinition: actionDefinition,
                    appSchema: importParameters.jsonSchema,
                    factory,
                    actionsInConfig: configActionsPart,
                    actionKey,
                    manifest: importParameters.manifest,
                    routingId: importParameters.routingId,
                    manifestSectionId
                };
                importActionSettingsOfReference(importActionParams);
            }
        }
    }
}

/**
 * Fills custom fields in config.json.
 *
 * @param importParameters - object comprising all input data
 * @param manifestSection - section of the manifest.json containing the relevant settings
 * @param sectionId - ID of the current section
 * @param configPart - the given part of the object page config
 * @param factory - factory, for generating the access to reflect-metadata
 * @param manifestSectionId - ID of the section in the manifest
 * @param sectionDefinitionName - definition key for fields section
 */
function fillFieldInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSection: ManifestContentSegment,
    sectionId: string,
    configPart: object,
    factory: MetadataInstanceInterface,
    manifestSectionId: string,
    sectionDefinitionName = DefinitionName.ObjectPageSectionForm
): void {
    if (manifestSection['fields']) {
        const forms = importParameters.jsonSchema.definitions[
            `${sectionDefinitionName}<${getDefinitionId(manifestSectionId)}>`
        ] as Definition;
        const formReference = forms.properties.form['$ref'].split(DEFINITION_LINK_PREFIX)[1];
        const form = importParameters.jsonSchema.definitions[formReference] as Definition;
        const fields = form.properties['fields'] as Definition;
        const fieldsReference = fields.$ref.split(DEFINITION_LINK_PREFIX)[1];
        const fieldsDefinition = importParameters.jsonSchema.definitions[fieldsReference] as Definition;

        for (const fieldKey in fieldsDefinition['properties']) {
            const fieldDefinition = fieldsDefinition.properties[fieldKey] as Definition;
            const definitionKey = getDefinitionKey(fieldDefinition.$ref);
            const formFieldDefinition = importParameters.jsonSchema.definitions[definitionKey] as Definition;
            if (!('annotationPath' in formFieldDefinition) && formFieldDefinition.description === 'Custom Field') {
                configPart[sectionId].form ??= {};
                configPart[sectionId].form.fields ??= {};
                const schemaPropertyName = definitionKey.split('<')[0];
                const fieldInstance = factory.createInstance(PageTypeV4.ObjectPage, schemaPropertyName);
                configPart[sectionId].form.fields[fieldKey] = fieldInstance;
                importSettingsOfObject(
                    fieldInstance,
                    importParameters.manifest,
                    formFieldDefinition,
                    importParameters.routingId,
                    [fieldKey],
                    sectionId,
                    undefined,
                    importParameters.jsonSchema.definitions
                );
            }
        }
    }
}

/**
 * Fills the table settings in config.json.
 *
 * @param importParameters  - object comprising all input data
 * @param manifestSettings - object page settings section of the manifest
 * @param configPart - the given part of the object page config
 * @param sectionId - ID of the current section
 * @param factory - factory, for generating the access to reflect-metadata
 * @param tableId - ID of the table in the manifest, if available
 */
function fillTableInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSettings: v4.SapUiPageSettingsV4,
    configPart: object,
    sectionId: string,
    factory: MetadataInstanceInterface,
    tableId?: string
) {
    const manifestSectionId = tableId ? tableId : getManifestId(sectionId, false, UIAnnotationTerms.LineItem);
    const manifestSection = manifestSettings.controlConfiguration?.[manifestSectionId];

    if (manifestSection) {
        // All table types now use the same ObjectPageTable definition
        configPart[sectionId] = configPart[sectionId] ?? {};
        configPart[sectionId].table = factory.createInstance(
            PageTypeV4.ObjectPage,
            DefinitionName.ObjectPageTable,
            configPart[sectionId].table
        );

        const sectionDefinition = `${DefinitionName.ObjectPageTable}<${
            tableId ? getDefinitionId(tableId) : sectionId.replace('#', '::')
        }>`;
        importSettingsOfObject(
            configPart[sectionId].table,
            importParameters.manifest,
            importParameters.jsonSchema.definitions[sectionDefinition],
            importParameters.routingId,
            [],
            manifestSectionId,
            undefined,
            importParameters.jsonSchema.definitions
        );

        configPart[sectionId].table.toolBar = factory.createInstance(PageTypeV4.ObjectPage, DefinitionName.ToolBar, {
            actions: {}
        });
        const actionsDefinition = importParameters.jsonSchema.definitions[
            `${DefinitionName.Actions}<${getDefinitionId(manifestSectionId)}>`
        ] as Definition;
        fillSectionActionsInConfig(
            importParameters,
            actionsDefinition,
            sectionId,
            configPart,
            factory,
            manifestSectionId
        );
        fillTableColumnsInConfig(importParameters, manifestSection, configPart, sectionId, factory, manifestSectionId);
    }
}

/**
 * Fills the form settings in config.json.
 *
 * @param importParameters  - object comprising all input data
 * @param manifestSettings - object page settings section of the manifest
 * @param configPart - the given part of the object page config
 * @param sectionId - ID of the current section
 * @param factory - factory, for generating the access to reflect-metadata
 * @param includeActions - Whether to include action configurations in the form(default is 'true')
 */
function fillFormInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSettings: v4.SapUiPageSettingsV4,
    configPart: object,
    sectionId: string,
    factory: MetadataInstanceInterface,
    includeActions = true
) {
    const manifestSectionId = getManifestId(sectionId);
    const manifestSection = manifestSettings.controlConfiguration?.[manifestSectionId];
    if (manifestSection) {
        configPart[sectionId] = {};
        const initialConfig = includeActions ? { actions: {} } : {};
        configPart[sectionId].form = factory.createInstance(
            PageTypeV4.ObjectPage,
            DefinitionName.ObjectPageForm,
            initialConfig
        );
        if (includeActions) {
            fillFormActionsInConfig(
                importParameters,
                manifestSection,
                sectionId,
                configPart,
                factory,
                manifestSectionId
            );
        }
        // fill custom fields
        fillFieldInConfig(importParameters, manifestSection, sectionId, configPart, factory, manifestSectionId);
    }
}

/**
 * Fills the custom sub section in config.json.
 *
 * @param importParameters Object comprising all input data
 * @param schemaSection Section schema
 * @param configPart The given part of the object page config
 * @param sectionId ID of the current section
 * @param factory Factory, for generating the access to reflect-metadata
 * @param parentId Parent section id
 */
function fillCustomSubSectionInConfig(
    importParameters: ImportObjectPageV4Parameters,
    schemaSection: Definition,
    configPart: object,
    sectionId: string,
    factory: MetadataInstanceInterface,
    parentId?: string
) {
    const section = factory.createInstance(PageTypeV4.ObjectPage, DefinitionName.ObjectPageCustomSubSectionFragment);
    const sectionIdRef = schemaSection['$ref'].split(DEFINITION_LINK_PREFIX)[1];
    importSettingsOfObject(
        section,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[sectionIdRef],
        importParameters.routingId,
        [`subSections/${sectionId}`],
        parentId
    );
    if (section.fragmentName) {
        const customPageConfig = createCustomFragmentConfig(importParameters, section.fragmentName);
        section.controls = customPageConfig;
    }
    configPart[sectionId] = section;
}

/**
 * Fills the sections and subsections of the configuration (recursive function).
 *
 * @param {ImportObjectPageV4Parameters} importParameters - object comprising all input data
 * @param sectionProperties - current properties' list (of sections or subsections)
 * @param manifestSettings - object page settings section of the manifest
 * @param configPart - the given part of the object page config
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param parentId - parent section id
 */
function fillSectionsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    sectionProperties: Definition,
    manifestSettings: v4.SapUiPageSettingsV4,
    configPart: Definition,
    factory: MetadataInstanceInterface,
    parentId?: string
): void {
    for (const sectionId in sectionProperties) {
        const schemaSection: Definition = sectionProperties[sectionId];
        let tableManifestId = sectionId;
        if (
            (sectionId.includes(UIAnnotationTerms.PresentationVariant) ||
                sectionId.includes(UIAnnotationTerms.SelectionPresentationVariant)) &&
            schemaSection.$ref?.startsWith(DEFINITION_LINK_PREFIX + DefinitionName.ObjectPageSectionTableV4)
        ) {
            const sectionIdRef = schemaSection['$ref'].split(DEFINITION_LINK_PREFIX)[1];
            tableManifestId = getManifestId(sectionIdRef, true, UIAnnotationTerms.LineItem);
            fillTableInConfig(importParameters, manifestSettings, configPart, sectionId, factory, tableManifestId);
        } else if (schemaSection.$ref?.startsWith(DEFINITION_LINK_PREFIX + DefinitionName.ObjectPageSectionTableV4)) {
            // Section without subsections
            fillTableInConfig(importParameters, manifestSettings, configPart, sectionId, factory);
        } else if (schemaSection.properties && schemaSection.properties.subsections) {
            configPart[sectionId] = {
                subsections: {}
            };
            fillSectionsInConfig(
                importParameters,
                schemaSection.properties.subsections['properties'],
                manifestSettings,
                configPart[sectionId].subsections,
                factory,
                sectionId
            );
        } else if (
            schemaSection.$ref?.startsWith(DEFINITION_LINK_PREFIX + DefinitionName.ObjectPageSectionForm) &&
            (schemaSection.$ref?.includes(`@${UIAnnotationTerms.FieldGroup}::`) ||
                schemaSection.$ref?.includes(`@${UIAnnotationTerms.Identification}`))
        ) {
            // Form section
            fillFormInConfig(
                importParameters,
                manifestSettings,
                configPart,
                sectionId,
                factory,
                !schemaSection.$ref.includes(`@${UIAnnotationTerms.Identification}`)
            );
        } else if (
            schemaSection.$ref?.startsWith(DEFINITION_LINK_PREFIX + DefinitionName.ObjectPageCustomSubSectionFragment)
        ) {
            fillCustomSubSectionInConfig(importParameters, schemaSection, configPart, sectionId, factory, parentId);
        }
    }
}

/**
 * Fills the custom sections of the configuration.
 *
 * @param {ImportObjectPageV4Parameters} importParameters - object comprising all input data
 * @param manifestSettings - object page settings section of the manifest
 * @param objectPageConfig - the given part of the object page config
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 */
function fillCustomSectionsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSettings: v4.SapUiPageSettingsV4,
    objectPageConfig: v4.ObjectPageConfigV4,
    factory: MetadataInstanceInterface
): void {
    const customSections = manifestSettings.content?.body?.sections;
    if (customSections) {
        for (const sectionId in customSections) {
            const customSection = customSections[sectionId];
            if ('subSections' in customSection) {
                //'subSections' is reserved property - It is not custom section, but object with custom subsections
                continue;
            }
            const section = factory.createInstance(
                PageTypeV4.ObjectPage,
                DefinitionName.ObjectPageCustomSectionFragment
            );
            importSettingsOfObject(
                section,
                importParameters.manifest,
                importParameters.jsonSchema.definitions[DefinitionName.ObjectPageCustomSectionFragment],
                importParameters.routingId,
                [],
                sectionId
            );
            // fill custom section with custom actions
            if (customSection.actions) {
                section.actions = factory.createInstance(
                    PageTypeV4.ObjectPage,
                    DefinitionName.ObjectPageCustomSectionActions,
                    {}
                );
                const actionsDefinition = importParameters.jsonSchema.definitions[
                    `${DefinitionName.ObjectPageCustomSectionActions}<${getDefinitionId(sectionId)}>`
                ] as Definition;
                fillSectionActionsInConfig(importParameters, actionsDefinition, sectionId, section, factory, sectionId);
            }
            const fragmentName = customSection.template;
            if (fragmentName) {
                const customPageConfig = createCustomFragmentConfig(importParameters, fragmentName);
                section.controls = customPageConfig;
            }
            objectPageConfig.sections[sectionId] = section;
        }
    }
}

/**
 * Fills the custom header sections of the configuration.
 *
 * @param {ImportObjectPageV4Parameters} importParameters - object comprising all input data
 * @param {v4.SapUiPageSettingsV4} manifestSettings - object page settings header section of the manifest
 * @param {v4.ObjectPageConfigV4} objectPageConfig - the given part of the object page config
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 */
function fillCustomHeaderSectionsInConfig(
    importParameters: ImportObjectPageV4Parameters,
    manifestSettings: v4.SapUiPageSettingsV4,
    objectPageConfig: v4.ObjectPageConfigV4,
    factory: MetadataInstanceInterface
): void {
    const header = manifestSettings.content?.header;
    const customHeaderSections = header && 'facets' in header ? header.facets : undefined;
    if (customHeaderSections) {
        for (const sectionId in customHeaderSections as object) {
            const customHeaderSection = customHeaderSections[sectionId];
            const section = factory.createInstance(
                PageTypeV4.ObjectPage,
                DefinitionName.ObjectPageCustomHeaderSectionFragment
            );
            importSettingsOfObject(
                section,
                importParameters.manifest,
                importParameters.jsonSchema.definitions[DefinitionName.ObjectPageCustomHeaderSectionFragment],
                importParameters.routingId,
                [],
                sectionId
            );
            // assign fragment contents in config
            const readFragmentName = customHeaderSection.template;
            if (readFragmentName) {
                const customPageConfig = createCustomFragmentConfig(importParameters, readFragmentName);
                section.controls = customPageConfig;
            }
            const editFragmentName = customHeaderSection.templateEdit;
            if (editFragmentName) {
                const customPageConfig = createCustomFragmentConfig(importParameters, editFragmentName);
                section.controlsEditMode = customPageConfig;
            }
            objectPageConfig.header.sections[sectionId] = section;
        }
    }
}

/**
 * Fills the header facets in the config file of a V4 object page.
 *
 * @param appSchema - app specific schema
 * @param objectPageConfig - configuration, to be enhanced
 * @param factory - factory, for generating the access to reflect-metadata
 * @param manifest - manifest.json of the app
 * @param manifestSettings - object page settings section of the manifest
 * @param routingId - routing ID of the page
 */
function fillHeaderFacets(
    appSchema: Definition,
    objectPageConfig: v4.ObjectPageConfigV4,
    factory: MetadataInstanceInterface,
    manifest: Manifest,
    manifestSettings: v4.SapUiPageSettingsV4,
    routingId: string
): void {
    const headerSections = appSchema.definitions[DefinitionName.HeaderSections] as Definition;
    if (headerSections && headerSections.properties) {
        objectPageConfig.header.sections = {};
        for (const sectionId in headerSections.properties) {
            let sectionDefinition = headerSections.properties[sectionId];
            let factoryKey;
            const sectionDefinitionKey = headerSections.properties[sectionId]['$ref']?.split(DEFINITION_LINK_PREFIX)[1];
            if (sectionDefinitionKey) {
                sectionDefinition = appSchema.definitions[sectionDefinitionKey];
                factoryKey = sectionDefinitionKey.split('<')[0];
            }
            const isCustomHeaderSection = factoryKey?.includes(DefinitionName.ObjectPageCustomHeaderSectionFragment);
            if (!isCustomHeaderSection) {
                objectPageConfig.header.sections[sectionId] = factory.createInstance(PageTypeV4.ObjectPage, factoryKey);
                importSettingsOfObject(
                    objectPageConfig.header.sections[sectionId],
                    manifest,
                    sectionDefinition,
                    routingId,
                    [],
                    (sectionDefinition as Definition)?.title?.split(FACETTITLEPREFIX)[1]
                );
                // fill custom fields
                const manifestSectionId = getManifestId(sectionId);
                const manifestSection = manifestSettings.controlConfiguration?.[manifestSectionId];
                if (manifestSection) {
                    fillFieldInConfig(
                        {
                            jsonSchema: appSchema,
                            manifest,
                            routingId
                        },
                        manifestSection,
                        sectionId,
                        objectPageConfig.header.sections,
                        factory,
                        manifestSectionId,
                        DefinitionName.ObjectPageHeaderSectionForm
                    );
                }
            }
        }
    }
}

/**
 * Fills the header and footer actions in the config file of a V4 object page.
 *
 * @param {Definition} appSchema - app specific schema
 * @param {v4.ObjectPageConfigV4} objectPageConfig - configuration, to be enhanced
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param {Manifest} manifest - manifest.json of the app
 * @param {string} routingId - routing id of page
 * @param {DefinitionName} definitionName definition name in the schema
 * @param {PropertyName} actionPropertyName - header or a footer action
 */
function fillHeaderFooterActions(
    appSchema: Definition,
    objectPageConfig: v4.ObjectPageConfigV4,
    factory: MetadataInstanceInterface,
    manifest: Manifest,
    routingId: string,
    definitionName: DefinitionName,
    actionPropertyName: PropertyName
): void {
    const actions = appSchema.definitions[definitionName] as Definition;
    if (typeof actions === 'object') {
        const actionsInConfig = (objectPageConfig[actionPropertyName].actions = {});
        for (const actionKey in actions.properties) {
            const definition = actions.properties[actionKey];
            if (typeof definition === 'object' && definition.$ref) {
                const importActionParams: ImportActionSettingsOfReferenceParams = {
                    actionPropertyDefinition: definition,
                    appSchema,
                    factory,
                    actionsInConfig,
                    actionKey,
                    manifest,
                    routingId
                };
                importActionSettingsOfReference(importActionParams);
            }
        }
    }
}

/**
 * Fills the object page configuration from the given manifest.
 *
 * @param importParameters  - object comprising all input data
 * @param v4Page - current (object) page
 * @param factory - factory, for generating the access to reflect-metadata
 * @returns the object page configuration
 */
function fillObjectPageConfig(
    importParameters: ImportObjectPageV4Parameters,
    v4Page: v4.SapUiAppPageV4,
    factory: MetadataInstanceInterface
): v4.ObjectPageConfigV4 {
    const objectPageConfig: v4.ObjectPageConfigV4 = factory.createPageInstance(PageTypeV4.ObjectPage);

    const manifestSettings = v4Page.options && v4Page.options.settings;

    // Transfer header information
    objectPageConfig.header = factory.createInstance(PageTypeV4.ObjectPage, DefinitionName.ObjectPageHeader);
    importSettingsOfObject(
        objectPageConfig.header,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.ObjectPageHeader],
        importParameters.routingId,
        []
    );

    // Transfer footer information
    objectPageConfig.footer = factory.createInstance(PageTypeV4.ObjectPage, DefinitionName.ObjectPageFooter);
    importSettingsOfObject(
        objectPageConfig.footer,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.ObjectPageFooter],
        importParameters.routingId,
        []
    );

    // Transfer layout information
    objectPageConfig.layout = factory.createInstance(PageTypeV4.ObjectPage, DefinitionName.ObjectPageLayout);
    importSettingsOfObject(
        objectPageConfig.layout,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.ObjectPageLayout],
        importParameters.routingId,
        []
    );

    importSettingsOfObject(
        objectPageConfig,
        importParameters.manifest,
        importParameters.jsonSchema,
        importParameters.routingId,
        []
    );
    if (!objectPageConfig.sections) {
        objectPageConfig.sections = {};
    }
    if (manifestSettings) {
        if (
            (manifestSettings.controlConfiguration || manifestSettings.content?.body?.sections) &&
            importParameters.jsonSchema.definitions[DefinitionName.Sections]
        ) {
            // Fill sections and subsections
            objectPageConfig.sections = {};
            fillSectionsInConfig(
                importParameters,
                importParameters.jsonSchema.definitions[DefinitionName.Sections]['properties'],
                manifestSettings,
                objectPageConfig.sections as Definition,
                factory
            );
        }
        // Fill custom sections
        fillCustomSectionsInConfig(importParameters, manifestSettings, objectPageConfig, factory);
        // Fill header facets
        fillHeaderFacets(
            importParameters.jsonSchema,
            objectPageConfig,
            factory,
            importParameters.manifest,
            manifestSettings,
            importParameters.routingId
        );
        // Fill custom header sections
        fillCustomHeaderSectionsInConfig(importParameters, manifestSettings, objectPageConfig, factory);
        fillHeaderFooterActions(
            importParameters.jsonSchema,
            objectPageConfig,
            factory,
            importParameters.manifest,
            importParameters.routingId,
            DefinitionName.ObjectPageHeaderActions,
            PropertyName.header
        );
        fillHeaderFooterActions(
            importParameters.jsonSchema,
            objectPageConfig,
            factory,
            importParameters.manifest,
            importParameters.routingId,
            DefinitionName.FooterActions,
            PropertyName.footer
        );
    }
    return objectPageConfig;
}

/**
 * External API: Create configuration (file content) for a V4 object page.
 *
 * @param importParameters  - object comprising all input data
 * @returns config - the object page configuration
 */
export function createObjectPageConfigV4(
    importParameters: ImportObjectPageV4Parameters
): v4.ObjectPageConfigV4 | undefined {
    // Initialize i18next
    initI18n();
    if (!importParameters.manifest[ManifestSection.ui5] || !importParameters.manifest[ManifestSection.ui5]['routing']) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOROUTING'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5]
            }
        });
        return;
    }
    const v4Pages = importParameters.manifest[ManifestSection.ui5]['routing']['targets'] as v4.SapUi5RoutingTargetsV4;
    if (!v4Pages) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOTARGETS'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5, 'routing']
            }
        });
        return;
    }
    const v4Page = v4Pages[importParameters.routingId];
    if (!v4Page) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOROUTINGID', { routingId: importParameters.routingId }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5, 'routing']
            }
        });
        return;
    }
    const factory = new Factory();

    //Fill config.json
    return fillObjectPageConfig(importParameters, v4Page as v4.SapUiAppPageV4, factory);
}
